/**
 * GST Type & Split Calculation Utilities
 * As per GST Statutory Rules (Section 5: IGST vs CGST+SGST)
 *
 * Rule:
 * CGST + SGST = Intra-state (within same state) — split 50-50
 * IGST = Inter-state (different states) — full amount
 *
 * Supplier's state = active warehouse branch stateCode
 * Customer's state = from customer GSTIN first 2 digits (or default CGST+SGST for B2C unregistered)
 */

export function getGSTType(supplierStateCode?: string | null, customerGSTIN?: string | null): 'CGST_SGST' | 'IGST' {
  if (!customerGSTIN || typeof customerGSTIN !== 'string' || customerGSTIN.trim().length < 2) {
    return 'CGST_SGST'; // B2C default based on supplier state
  }

  const cleanGSTIN = customerGSTIN.trim();
  const customerStateCode = cleanGSTIN.substring(0, 2);
  const cleanSupplierState = (supplierStateCode || '').trim();

  if (!cleanSupplierState) {
    return 'CGST_SGST';
  }

  return cleanSupplierState === customerStateCode ? 'CGST_SGST' : 'IGST';
}

export function calculateGST(
  taxable: number,
  rate: number,
  gstType: 'CGST_SGST' | 'IGST' | string,
): { cgst: number; sgst: number; igst: number; totalTax: number } {
  const taxableNum = Number(taxable) || 0;
  const rateNum = Number(rate) || 0;
  const total = Number(((taxableNum * rateNum) / 100).toFixed(2));

  if (gstType === 'IGST') {
    return {
      cgst: 0,
      sgst: 0,
      igst: total,
      totalTax: total,
    };
  }

  const half = Number((total / 2).toFixed(2));
  return {
    cgst: half,
    sgst: half,
    igst: 0,
    totalTax: total,
  };
}

export function isValidGSTIN(gstin?: string | null): boolean {
  if (!gstin || typeof gstin !== 'string') return false;
  // GSTIN Pattern: 2 digits + 5 alpha + 4 numeric + 1 alpha + 1 alphanumeric entity + 'Z' + 1 check digit
  const gstinRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/i;
  return gstinRegex.test(gstin.trim());
}

export interface EInvoiceQRPayloadParams {
  sellerGstin?: string;
  buyerGstin?: string;
  docNo?: string;
  docType?: string;
  docDate?: string;
  totInvVal?: number;
  itemCnt?: number;
  mainHsnCode?: string;
  irn?: string;
  ackNo?: string | number;
  ackDate?: string;
  status?: string;
}

/**
 * Builds structured GST E-Invoice QR text that scannable phones/apps can read clearly
 */
export function buildEInvoiceQRText(data: EInvoiceQRPayloadParams): string {
  const parts: string[] = [
    `GST E-INVOICE VERIFICATION`,
    `--------------------------`,
    `IRN: ${data.irn || '—'}`,
    `Ack No: ${data.ackNo || '—'}`,
    `Ack Date: ${data.ackDate || '—'}`,
    `Invoice No: ${data.docNo || '—'}`,
    `Invoice Date: ${data.docDate || '—'}`,
    `Supplier GSTIN: ${data.sellerGstin || '—'}`,
    `Buyer GSTIN: ${data.buyerGstin || '—'}`,
    `Total Amount: ₹${(Number(data.totInvVal) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`,
    `Items: ${data.itemCnt || 1}`,
    `Main HSN: ${data.mainHsnCode || '—'}`,
    `Status: ${data.status || 'ACTIVE (IRP VERIFIED)'}`,
    `--------------------------`,
    `GST Portal: https://einvoice1.gst.gov.in`,
  ];
  return parts.join('\n');
}

/**
 * Returns a public verification URL that phone scanners open to display the full verified invoice
 */
export function getEInvoicePublicVerifyUrl(irnOrAckOrInvoiceNo?: string): string {
  const code = (irnOrAckOrInvoiceNo || 'a1b2c3d4e5f6a1b2c3d4e5f6a1b2c3d4e5f6a1b2c3d4e5f6a1b2c3d4e5f6a1b2').trim();
  const origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost:5173';
  return `${origin}/verify/e-invoice/${encodeURIComponent(code)}`;
}

